import {
  IconButton,
  ThumbsDownFilledIcon,
  ThumbsDownIcon,
  ThumbsUpFilledIcon,
  ThumbsUpIcon,
} from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'

import type { TakeawayFeedback } from '../../graphqlEnums'
import { useSetTakeawayFeedback } from '../../hooks/useSetTakeawayFeedback'
import type { TakeawayCtaTrackingContext } from '../../hooks/useTrackTakeawayCtaClicked'
import { useTrackTakeawayFeedbackClicked } from '../../hooks/useTrackTakeawayFeedbackClicked'

import styles from './Takeaways.module.css'

import type { JSX } from 'react'

interface TakeawayFeedbackControlsProps {
  takeawayId: string
  /**
   * Channel the vote is authorized against. On a cross-channel takeaway any
   * channel it covers works — `setTakeawayFeedback` checks membership.
   */
  channelId?: string | null
  feedback?: TakeawayFeedback | null
  /** Per-card analytics context, shared with CTA click tracking. */
  trackingContext: TakeawayCtaTrackingContext
}

export function TakeawayFeedbackControls({
  takeawayId,
  channelId,
  feedback,
  trackingContext,
}: TakeawayFeedbackControlsProps): JSX.Element | null {
  const organizationId = useOrganizationId()
  const { setFeedback } = useSetTakeawayFeedback()
  const trackFeedbackClicked = useTrackTakeawayFeedbackClicked()

  if (!organizationId || !channelId) {
    return null
  }

  // Persist the new value and track the interaction together. `next` is null
  // when the user clicks the active option again to clear their selection.
  const handleFeedback = (next: TakeawayFeedback | null): void => {
    void setFeedback({ takeawayId, channelId, organizationId, feedback: next })
    trackFeedbackClicked({ ...trackingContext, feedback: next })
  }

  return (
    <>
      <IconButton
        label="This was helpful"
        tooltip="This was helpful"
        variant="tertiary"
        size="small"
        aria-pressed={feedback === 'helpful'}
        className={styles.cardActionButton}
        onClick={() =>
          handleFeedback(feedback === 'helpful' ? null : 'helpful')
        }
      >
        {feedback === 'helpful' ? (
          <ThumbsUpFilledIcon size="xsmall" />
        ) : (
          <ThumbsUpIcon size="xsmall" />
        )}
      </IconButton>
      <IconButton
        label="This was not helpful"
        tooltip="This was not helpful"
        variant="tertiary"
        size="small"
        aria-pressed={feedback === 'unhelpful'}
        className={styles.cardActionButton}
        onClick={() =>
          handleFeedback(feedback === 'unhelpful' ? null : 'unhelpful')
        }
      >
        {feedback === 'unhelpful' ? (
          <ThumbsDownFilledIcon size="xsmall" />
        ) : (
          <ThumbsDownIcon size="xsmall" />
        )}
      </IconButton>
    </>
  )
}
